/**
 * The one budget primitive every vendor session is bounded by.
 *
 * It was three private copies - `raceElapsedAgainst` in mindset-session.ts, `raceElapsed` in
 * shenai-session.ts, and it would have been a third in hybrid-session.ts. One function rather than
 * three because there is exactly one rule here and every vendor path applies it: a vendor gets a
 * window, and a window that closes is not an exception - it is an answer the caller decides what to
 * do with. That is why this RESOLVES with `'elapsed'` rather than rejecting: a timeout that throws
 * arrives at the same `catch` as a vendor that crashed, and the two are different scans.
 */
export const ELAPSED = 'elapsed' as const

/**
 * Whichever comes first: the promise settling, or `ms` passing.
 *
 * @param ms The window, in milliseconds.
 * @param settled The work being bounded.
 * @returns What `settled` resolved with, or `'elapsed'` if the window closed first. A REJECTED
 * `settled` still rejects - a vendor that fails inside its window is not a vendor that ran out of
 * time, and the caller's own catch is where that difference is handled.
 */
export async function raceElapsed<T>(ms: number, settled: Promise<T>): Promise<T | typeof ELAPSED> {
  return Promise.race([
    new Promise<typeof ELAPSED>((resolve) => setTimeout(() => resolve(ELAPSED), ms)),
    settled,
  ])
}
